import React from 'react';
import { formatPhone } from '../utils/formatPhone';
import { SEASON_FILTERS } from '../utils/recipientBuckets';
import { resubscribeConfirmText } from '../utils/emailUnsubscribe';

// Step 1 of the Email Customers modal: the channel (when texting is on), the
// recipient filter, the manual list and the count line. Extracted from
// EmailCustomersModal so the modal stays readable; the modal owns the state
// and computes the buckets (utils/recipientBuckets.js), this only renders.
//
// Sentinel for "customer has no pricing tier". Safe against collision because
// every pricing_tiers.id is a positive integer.
export const CUSTOM_TIER = 'custom';

const CHANNEL_LABELS = { email: 'Email', text: 'Text', both: 'Both' };

function RecipientPicker({
  channel, onChannelChange, smsEnabled,
  buckets, eligible, excluded, recipients, split,
  filterMode, setFilterMode, filterRoute, setFilterRoute, filterTier, setFilterTier,
  routes, pricingTiers,
  selectedIds, onToggle, onSelectAll, onSelectNone,
  canSubscribe, subscribingId, onSubscribe, timezone,
}) {
  const unsubscribedWithNoEmail = buckets.email.unsubscribed.filter((c) => !buckets.hasEmail(c)).length;
  const textIds = new Set(buckets.text.eligible.map((c) => c.id));
  const emailIds = new Set(buckets.email.eligible.map((c) => c.id));

  const muted = { fontSize: '13px', color: 'var(--color-text-light)' };
  const bucketRow = { display: 'flex', flexWrap: 'wrap', gap: '2px 16px', marginTop: '8px', ...muted };

  return (
    <div>
      {smsEnabled && (
        <fieldset className="form-group" style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
          <legend className="form-label">Send as</legend>
          <div style={{ display: 'flex', gap: '8px' }} role="radiogroup" aria-label="Send as">
            {Object.keys(CHANNEL_LABELS).map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={channel === c}
                className={`btn btn-sm ${channel === c ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => onChannelChange(c)}
              >
                {CHANNEL_LABELS[c]}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      <div className="form-group">
        {/* htmlFor/id: the tests address each select by the name the owner
            sees rather than by position in the DOM. */}
        <label className="form-label" htmlFor="email-filter-mode">Filter Recipients</label>
        <select
          id="email-filter-mode"
          className="form-select"
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
        >
          <option value="all_opted_in">{channel === 'email' ? 'All opted-in customers' : 'Everyone who can be reached'}</option>
          <option value="active_only">Active customers only</option>
          {/* Hidden when there are no routes to filter by. The Customers page
              passes routes={[]} and GET /api/routes has no stops array, so the
              option would match nobody. A caller that passes routes with stops
              gets it back. */}
          {routes.length > 0 && <option value="by_route">By route</option>}
          <option value="by_tier">By pricing tier</option>
          {Object.entries(SEASON_FILTERS).map(([value, f]) => (
            <option key={value} value={value}>{f.label}</option>
          ))}
          <option value="selected">Manual selection</option>
        </select>
      </div>

      {filterMode === 'by_route' && (
        <div className="form-group">
          <label className="form-label" htmlFor="email-filter-route">Select Route</label>
          <select id="email-filter-route" className="form-select" value={filterRoute} onChange={(e) => setFilterRoute(e.target.value)}>
            <option value="">-- Select Route --</option>
            {routes.map((r) => (
              <option key={r.id} value={r.id}>{r.name || `Route ${r.id}`}</option>
            ))}
          </select>
        </div>
      )}

      {filterMode === 'by_tier' && (
        <div className="form-group">
          <label className="form-label" htmlFor="email-filter-tier">Select Tier</label>
          <select id="email-filter-tier" className="form-select" value={filterTier} onChange={(e) => setFilterTier(e.target.value)}>
            <option value="">-- Select Tier --</option>
            {pricingTiers.map((t) => (
              <option key={t.id} value={String(t.id)}>{t.name}</option>
            ))}
            <option value={CUSTOM_TIER}>Custom (no tier)</option>
          </select>
        </div>
      )}

      {filterMode === 'selected' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <button className="btn btn-sm btn-outline" onClick={onSelectAll}>Select All</button>
            <button className="btn btn-sm btn-outline" onClick={onSelectNone}>Clear</button>
          </div>
          <div style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
            {eligible.map((c) => (
              <label key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
              }}>
                <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => onToggle(c.id)} />
                <span>{c.first_name} {c.last_name}</span>
                <span style={{ color: 'var(--color-text-light)', fontSize: '12px', marginLeft: 'auto' }}>
                  {channel === 'email' && c.email}
                  {channel === 'text' && formatPhone(c.phone)}
                  {channel === 'both' && [emailIds.has(c.id) && 'Email', textIds.has(c.id) && 'Text'].filter(Boolean).join(' + ')}
                </span>
              </label>
            ))}

            {/* The excluded customers, with their reason. Not selectable: no
                onChange, the input is disabled, and the row is a div, not a
                label. The guarantee is structural: the modal's recipients
                are filtered FROM the eligible list and never see this one. */}
            {excluded.map(({ customer: c, reason }) => (
              <div
                key={`excluded-${c.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                  borderBottom: '1px solid #f0f0f0', cursor: 'default', background: '#fafafa',
                }}
              >
                <input type="checkbox" checked={false} disabled readOnly />
                <span style={{ color: 'var(--color-text-light)' }}>{c.first_name} {c.last_name}</span>
                <span style={{
                  marginLeft: 'auto', fontSize: '12px', color: 'var(--color-text-light)',
                  border: '1px solid var(--color-border)', borderRadius: '10px', padding: '1px 8px',
                  whiteSpace: 'nowrap',
                }}>{reason}</span>
                {/* Unsubscribed with an address only: a row with no email
                    stays unreachable whatever its flag says. */}
                {canSubscribe && channel === 'email' && reason === 'Unsubscribed' && buckets.hasEmail(c) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={() => {
                      // Only on the customer's request: they may have
                      // unsubscribed themselves (migration 112).
                      if (window.confirm(resubscribeConfirmText(c, timezone))) onSubscribe(c.id);
                    }}
                    disabled={subscribingId !== null}
                    aria-label={`Subscribe ${c.first_name} ${c.last_name}`}
                  >
                    {subscribingId === c.id ? 'Saving...' : 'Subscribe'}
                  </button>
                )}
              </div>
            ))}

            {eligible.length === 0 && excluded.length === 0 && (
              <div style={{ padding: '12px', color: 'var(--color-text-light)', fontSize: '13px' }}>
                No customers to show.
              </div>
            )}
          </div>
          {excluded.length > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '6px' }}>
              {channel === 'email' && 'Greyed rows cannot be emailed and are never included in a send.'}
              {channel === 'text' && 'Greyed rows cannot be texted and are never included in a send.'}
              {channel === 'both' && 'Greyed rows can be neither emailed nor texted and are never included in a send.'}
            </div>
          )}
        </div>
      )}

      {/* The count line, broken into disjoint buckets that sum to the total. */}
      <div style={{ background: '#f8f9fa', padding: '12px 16px', borderRadius: '8px', marginTop: '16px' }}>
        {channel === 'email' && <div><strong>{recipients.length}</strong> recipients will receive this email</div>}
        {channel === 'text' && <div><strong>{recipients.length}</strong> recipients will receive this text</div>}
        {channel === 'both' && (
          <div>
            <strong>{recipients.length}</strong> recipients: {split.email.length} by email, {split.text.length} by text
          </div>
        )}
        {channel !== 'text' && (
          <div style={bucketRow}>
            {channel === 'both' && <span>Email:</span>}
            <span>{buckets.email.eligible.length} can be emailed</span>
            <span>{buckets.email.noEmail.length} no email address</span>
            <span>{buckets.email.unsubscribed.length} unsubscribed</span>
            <span>{buckets.doNotServe.length} do not serve</span>
            <span>{buckets.addressable.length} customers total</span>
          </div>
        )}
        {channel !== 'email' && (
          <div style={bucketRow}>
            {channel === 'both' && <span>Text:</span>}
            <span>{buckets.text.eligible.length} can be texted</span>
            <span>{buckets.text.noConsent.length} no consent</span>
            <span>{buckets.text.noPhone.length} no mobile number</span>
            <span>{buckets.text.optedOut.length} opted out</span>
            <span>{buckets.doNotServe.length} do not serve</span>
            {channel === 'text' && <span>{buckets.addressable.length} customers total</span>}
          </div>
        )}
        {channel !== 'text' && unsubscribedWithNoEmail > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '4px' }}>
            {unsubscribedWithNoEmail} of the unsubscribed also have no email address, and are
            counted once, as unsubscribed.
          </div>
        )}
      </div>
    </div>
  );
}

export default RecipientPicker;
